import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  claimDiscovery,
  dismissDiscovery,
  getDiscoveryPreferences,
  type CatalogProvider,
  type DiscoveryPlacement,
  type IntegrationSurface,
} from "../../api/integrations";
import { getImpersonation } from "../../api/impersonation-state";
import { getUser } from "../../selectors/commonSelectors";
import { useTrackEvent } from "../../libs/analytics";
import {
  useExternalIntegrationsAccess,
  canManageIntegrations,
  customerCanManageIntegrations,
} from "../../pages/profile-settings/integrations-hub/access";
import { useIntegrationsCatalog } from "../../pages/profile-settings/integrations-hub/integrations-actions";
import {
  configuredProviders,
  discoveryCandidates,
  unconfiguredProviders,
  INTEGRATIONS_PATH,
} from "./model";
import {
  DISCOVERY_IMPRESSION_CAP,
  discoverySession,
  recentImpressions,
  recordImpression,
  reserveDiscovery,
  releaseDiscovery,
  dismissDiscoverySession,
  setDiscoveryAttribution,
  getDiscoveryAttribution,
  clearDiscoveryAttribution,
} from "./session";

export function useDiscoveryCatalog() {
  const user = useSelector(getUser);
  const hasAccess = useExternalIntegrationsAccess(user);
  const enabled = Boolean(user?.userId) && hasAccess;
  const catalog = useIntegrationsCatalog({ enabled });
  return {
    user,
    enabled: enabled && !(catalog.isError && !catalog.data),
    canManage: Boolean(user && canManageIntegrations(user)),
    catalog,
  };
}

export function useIntegrationDiscovery(
  placement: DiscoveryPlacement,
  active = true
) {
  const { user, enabled, canManage, catalog } = useDiscoveryCatalog();
  const queryClient = useQueryClient();
  const track = useTrackEvent();
  const navigate = useNavigate();
  const userId = user?.userId ?? "";
  const providers = catalog.data ?? [];
  const previewing = Boolean(getImpersonation());
  const customerCanManage = Boolean(
    user && customerCanManageIntegrations(user)
  );
  const surface: IntegrationSurface | undefined =
    placement === "ideas" || placement === "library" ? placement : undefined;
  const connected = configuredProviders(providers, surface);
  const pending = unconfiguredProviders(providers, surface);
  const candidates = discoveryCandidates(providers, surface);
  const preferencesKey = ["integration-discovery", userId, "preferences"];
  const preferences = useQuery({
    queryKey: preferencesKey,
    queryFn: getDiscoveryPreferences,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
  const preference = preferences.data?.find(
    (value) => value.placement === placement
  );
  const dismissed = Boolean(
    preference?.permanent ||
    (preference?.snoozedUntil &&
      Date.parse(preference.snoozedUntil) > Date.now())
  );
  const session = userId ? discoverySession(userId) : null;
  const claimKey = [
    "integration-discovery",
    userId,
    "claim",
    session?.id,
    placement,
  ];
  // The banner already claimed this session stays up once it is counted.
  const capped =
    !previewing &&
    queryClient.getQueryData<{ show: boolean }>(claimKey)?.show !== true &&
    recentImpressions(userId, placement) >= DISCOVERY_IMPRESSION_CAP;
  const eligible =
    active &&
    enabled &&
    customerCanManage &&
    Boolean(catalog.data) &&
    preferences.isSuccess &&
    !dismissed &&
    candidates.length > 0 &&
    connected.length === 0 &&
    pending.length === 0 &&
    !capped &&
    !session?.dismissed &&
    (!session?.owner || session.owner === placement);
  const claim = useQuery({
    queryKey: claimKey,
    enabled: eligible && !previewing,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const reserved = reserveDiscovery(userId, placement);
      if (!reserved) return { show: false, newImpression: false };
      try {
        const result = await claimDiscovery(placement, reserved.id);
        if (!result.show) releaseDiscovery(userId, placement);
        if (result.newImpression) {
          recordImpression(userId, placement);
          track({
            eventName: "integration-discovery-impression",
            metadata: { placement, capability: surface ?? "all" },
            useServices: ["AMP"],
          });
        }
        return result;
      } catch (error) {
        releaseDiscovery(userId, placement);
        throw error;
      }
    },
  });
  const showDiscovery = eligible && (previewing || claim.data?.show === true);

  const dismiss = async (permanent: boolean) => {
    if (previewing) return;
    // Only persist the local dismissal after the server accepts it.
    try {
      await dismissDiscovery(placement, permanent);
      dismissDiscoverySession(userId);
      queryClient.setQueryData(claimKey, { show: false, newImpression: false });
      await queryClient.invalidateQueries({ queryKey: preferencesKey });
      track({
        eventName: "integration-discovery-dismiss",
        metadata: { placement, mode: permanent ? "permanent" : "snooze" },
        useServices: ["AMP"],
      });
    } catch {
      toast.error("Couldn't save your choice. Please try again.");
    }
  };
  const goToIntegrations = () => {
    if (!previewing) {
      setDiscoveryAttribution(userId, {
        placement,
        surface,
        baseline: configuredKeys(providers),
        startedAt: Date.now(),
      });
      track({
        eventName: "integration-discovery-connect-click",
        metadata: { placement, capability: surface ?? "all" },
        useServices: ["AMP"],
      });
    }
    navigate(INTEGRATIONS_PATH);
  };
  return {
    enabled,
    canManage,
    customerCanManage,
    providers,
    candidates,
    connected,
    pending,
    surface,
    showDiscovery,
    previewing,
    dismiss,
    goToIntegrations,
    // Used by the optional onboarding step. A read/claim error must never lock
    // users out of creating content; the next session can retry discovery.
    isLoading:
      enabled &&
      customerCanManage &&
      active &&
      (catalog.isPending ||
        preferences.isPending ||
        (eligible && !previewing && claim.isPending)),
    complete:
      Boolean(
        session?.dismissed || (session?.owner && session.owner !== placement)
      ) ||
      !enabled ||
      !customerCanManage ||
      dismissed ||
      connected.length > 0 ||
      pending.length > 0 ||
      capped ||
      (claim.isSuccess && !claim.data.show) ||
      claim.isError ||
      preferences.isError ||
      catalog.isError ||
      (catalog.isSuccess && candidates.length === 0),
    dismissed,
  };
}

function configuredKeys(providers: CatalogProvider[]) {
  return providers.flatMap((provider) =>
    (provider.connection?.configuredCapabilities ?? []).map(
      (cap) => `${provider.key}/${cap.capability}`
    )
  );
}

// Authorization alone isn't completion: retain attribution until a capability
// is configured and active/syncing. This also covers RSS's silent connection.
export function useDiscoveryCompletion(
  providers: CatalogProvider[] | undefined
) {
  const user = useSelector(getUser);
  const track = useTrackEvent();
  useEffect(() => {
    if (!user?.userId || !providers) return;
    const source = getDiscoveryAttribution(user.userId);
    if (!source) return;
    for (const provider of providers) {
      if (!["active", "syncing"].includes(provider.connection?.status ?? ""))
        continue;
      const newlyConfigured = provider.connection?.configuredCapabilities?.find(
        (cap) =>
          cap.enabled &&
          (!source.surface ||
            cap.capability.startsWith(`${source.surface}:`)) &&
          !source.baseline.includes(`${provider.key}/${cap.capability}`)
      );
      if (newlyConfigured) {
        clearDiscoveryAttribution(user.userId);
        track({
          eventName: "integration-discovery-connect-success",
          metadata: {
            placement: source.placement,
            provider: provider.key,
            capability: newlyConfigured.capability,
          },
          useServices: ["AMP"],
        });
        break;
      }
    }
  }, [providers, track, user?.userId]);
}
